import { Layout } from 'components/layout';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { EpmtyCard } from './empty-card';
import { Main } from './main';
import { TableReviews } from './reviews-table';
import { ProfileSection } from './section-profile';
import { TableReviewSlot } from './slot-table';

import { Loader } from 'common';
import { CardsMedium } from 'components/slider/cards/medium';
import { CardsMini } from 'components/slider/cards/mini';
import { useAuth } from 'context/auth';
import { useMeQuery, useUserByIdQuery } from 'hooks/api/users';

import { useCasinosQuery } from 'hooks/api/casino';
import { useStreamersQuery } from 'hooks/api/streamers';

import { CustomMetadata } from 'components/metadata';
import { Anchor } from 'constants/anchors';
import { allMetadata } from 'constants/metadata';
import { Routes } from 'constants/routes';
import {
  ICasinoReview,
  INavItem,
  IStreamer,
  IStreamerReview,
  IUser,
} from 'constants/types';

import styles from './styles.module.css';
import { Complaints, MyTopicsSection, Topics } from './topics';
import { NavigationBar } from '../casino/casino-navigations';
import { Icon } from 'common/icon';

const UserFavoritesCasinos = ({
  user,
  anchor,
}: {
  user?: IUser;
  anchor?: string;
}) => {
  return (
    <>
      {(user?.favorite_casinos?.length ?? 0) > 0 ? (
        <CardsMedium
          container="mini"
          hasFavoritesActions
          name="Saved Casinos"
          data={user?.favorite_casinos}
          count={user?.favorite_casinos?.length}
          anchor={anchor}
        />
      ) : (
        <EpmtyCard
          headerName="Saved Casinos"
          headerCount="0"
          textSearch="search casino"
          url="/casinos"
        />
      )}
    </>
  );
};

const UserFavoritesSlots = ({
  user,
  anchor,
}: {
  user?: IUser;
  anchor?: string;
}) => {
  return (
    <>
      {(user?.favorite_slots?.length ?? 0) > 0 ? (
        <CardsMini
          container="mini"
          name="Saved Slots"
          hasFavoritesActions
          data={user?.favorite_slots!!}
          count={user?.favorite_slots?.length?.toString() ?? '0'}
          anchor={anchor}
        />
      ) : (
        <EpmtyCard
          headerName="Saved Slots"
          headerCount="0"
          textSearch="search slot"
          url="/slots"
        />
      )}
    </>
  );
};

const UserFavoritesStreamers = ({
  user,
  anchor,
}: {
  user?: IUser;
  anchor?: string;
}) => {
  return (
    <>
      {(user?.favorite_streamers?.length ?? 0) > 0 ? (
        <CardsMedium
          container="mini"
          hasFavoritesActions
          type="streamer"
          name="Saved Streamers"
          data={user?.favorite_streamers}
          count={user?.favorite_streamers?.length}
          anchor={anchor}
        />
      ) : (
        <EpmtyCard
          headerName="Saved Streamers"
          headerCount="0"
          textSearch="search streamers"
          url={Routes.streamers}
        />
      )}
    </>
  );
};

const navItems: INavItem[] = [
  {
    text: 'Saved Casinos',
    anchor: `#${Anchor.SavedCasinos}`,
    icon: <Icon.Casinos />,
  },

  {
    text: 'Casino reviews',
    anchor: `#${Anchor.CasinoReviews}`,
    icon: <Icon.MessageFifth />,
  },

  {
    text: 'Slot reviews',
    anchor: `#${Anchor.slot.reviews}`,
    icon: <Icon.Dollar width={16} height={16} />,
  },

  {
    text: 'Topics',
    anchor: `#${Anchor.Topics}`,
    icon: <Icon.MessageFourth width={'16'} height={'16'} />,
  },
];

function ProfilePage() {
  const router = useRouter();
  const { id } = router.query;
  const { logout } = useAuth();

  const { data, error, isLoading } = useMeQuery(null, { skip: !!id });
  const { data: dataById, isFetching: isFetchingUserById } = useUserByIdQuery(
    { id: id?.toString() ?? '' },
    { skip: !id }
  );

  const { user } = data || dataById || {};

  const { data: { results: casinos } = {}, isFetching: isFetchingCasinos } =
    useCasinosQuery({});

  const { data: streamersData, isFetching: isFetchingStreamers } =
    useStreamersQuery({});
  const streamers = streamersData?.streamers ?? [];

  useEffect(() => {
    if (error) {
      logout();
      router.push(Routes.login);
    }
  }, [error, logout, router]);

  if (
    isLoading ||
    isFetchingUserById ||
    isFetchingCasinos ||
    isFetchingStreamers
  ) {
    return <Loader size="big" template="fullScreen" />;
  }

  return (
    <>
      <CustomMetadata metadata={allMetadata.myProfile} />
      <Layout hasFooter isSearchInputMobileVisible bgVariant="basic">
        <div className={styles.container}>
          <Main user={user} />
          <div className={styles.content}>
            <ProfileSection
              hasActions
              user={user}
              variant={id ? 'public' : 'private'}
            />
            {!id && (
              <NavigationBar
                arrow
                items={navItems}
                navItemClassName={styles.navItem}
                variantBg="notransparent"
                variantPadding="medium"
              />
            )}
            {!id && (
              <UserFavoritesCasinos user={user} anchor={Anchor.SavedCasinos} />
            )}
            {!id && <UserFavoritesSlots user={user} />}
            {/* {!id && <UserFavoritesStreamers user={user} />} */}

            {(user?.reviews?.length ?? 0) > 0 && (
              <div>
                <div id={Anchor.CasinoReviews} className="anchor" />

                <TableReviews
                  title={id ? 'Casino reviews' : 'My casino reviews'}
                  isPublicProfile={!!id}
                  type={'casino'}
                  data={
                    user?.reviews?.map((item) => {
                      const foundCasino = casinos?.find(
                        (casino) => casino._id === item.casino_id
                      );
                      return {
                        ...item,
                        user,
                        data: foundCasino,
                      } as ICasinoReview;
                    }) ?? []
                  }
                  anchor={Anchor.CasinoReviews}
                />
              </div>
            )}
            {(user?.streamers_reviews?.length ?? 0) > 0 && (
              <div>
                <div id={Anchor.StreamerReviews} className="anchor" />

                <TableReviews
                  title={'Streamers reviews'}
                  type={'streamer'}
                  data={
                    user?.streamers_reviews?.map((item) => {
                      const foundStreamer = streamers?.find(
                        (streamer: IStreamer) =>
                          streamer._id === item.streamer_id
                      );
                      return {
                        ...item,
                        user,
                        data: foundStreamer,
                      } as IStreamerReview;
                    }) ?? []
                  }
                />
              </div>
            )}
            <TableReviewSlot
              user={user}
              id={id}
              anchor={Anchor.slot.reviews}
              variant="slot"
              casinos={casinos}
            />
            {id && <MyTopicsSection anchor={Anchor.Topics} />}
            <Topics user={user} />

            <Complaints user={user} />
          </div>
        </div>
      </Layout>
    </>
  );
}

export default ProfilePage;
